import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../apiClient';

const TABS = [
  { id: 'cannibalisation', label: 'Cannibalisation' },
  { id: 'serp', label: 'Score SERP' },
  { id: 'maillage', label: 'Maillage interne' },
  { id: 'priorisation', label: 'Priorisation' },
  { id: 'searchconsole', label: 'Search Console' },
];

function scoreClass(score) {
  if (score >= 80) return 'good';
  if (score >= 60) return 'medium';
  return 'bad';
}

function opportunityLevel(score) {
  if (score >= 8) return 'urgent';
  if (score >= 5) return 'important';
  return 'normal';
}

function opportunityColor(level) {
  if (level === 'urgent') return '#ff6b6b';
  if (level === 'important') return '#f3c55d';
  return '#38d39f';
}

function SeoAnalyse() {
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [activeTab, setActiveTab] = useState('cannibalisation');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [cannibalisation, setCannibalisation] = useState({ groupes: [], recommandationsGlobales: [] });
  const [serpScores, setSerpScores] = useState([]);
  const [openedSerpId, setOpenedSerpId] = useState('');
  const [maillage, setMaillage] = useState([]);
  const [priorisation, setPriorisation] = useState([]);
  const [creatingTopicId, setCreatingTopicId] = useState('');
  const [createdTopicIds, setCreatedTopicIds] = useState([]);

  const [gscConnected, setGscConnected] = useState(null);
  const [gscSites, setGscSites] = useState([]);
  const [selectedGscProperty, setSelectedGscProperty] = useState('');
  const [gscStats, setGscStats] = useState(null);
  const [gscPages, setGscPages] = useState([]);
  const [gscLoading, setGscLoading] = useState(false);

  useEffect(() => {
    const loadSites = async () => {
      try {
        setLoading(true);
        const response = await apiFetch('/api/sites');
        if (!response.ok) throw new Error('Erreur chargement sites');
        const data = await response.json();
        const list = Array.isArray(data) ? data : [];
        setSites(list);
        if (list.length > 0) setSelectedSiteId(list[0].id);
      } catch {
        setError('Impossible de charger les sites.');
      } finally {
        setLoading(false);
      }
    };
    loadSites();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sc = params.get('searchConsole');
    if (sc === 'connected' || sc === 'error') {
      setActiveTab('searchconsole');
      if (sc === 'error') {
        setError('Connexion à Google Search Console impossible.');
      }
      window.history.replaceState({}, '', window.location.pathname || '/');
    }
  }, []);

  useEffect(() => {
    if (activeTab !== 'searchconsole') return;

    const loadGscSites = async () => {
      try {
        setGscLoading(true);
        setError('');
        const response = await apiFetch('/api/searchconsole/sites');
        if (response.status === 401) {
          setGscConnected(false);
          setGscSites([]);
          setSelectedGscProperty('');
          setGscStats(null);
          setGscPages([]);
          return;
        }
        if (!response.ok) throw new Error('Erreur Search Console');
        const data = await response.json();
        const list = Array.isArray(data?.sites) ? data.sites : [];
        setGscConnected(true);
        setGscSites(list);
        setSelectedGscProperty((current) => {
          if (current && list.some((s) => s.siteUrl === current)) return current;
          return list[0]?.siteUrl || '';
        });
      } catch {
        setError('Impossible de charger les propriétés Search Console.');
        setGscConnected(false);
      } finally {
        setGscLoading(false);
      }
    };

    loadGscSites();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'searchconsole' || !gscConnected || !selectedGscProperty) {
      return;
    }

    const encoded = encodeURIComponent(selectedGscProperty);
    let cancelled = false;

    const loadGscData = async () => {
      try {
        setGscLoading(true);
        setGscStats(null);
        setGscPages([]);
        const [statsRes, pagesRes] = await Promise.all([
          apiFetch(`/api/searchconsole/stats/${encoded}`),
          apiFetch(`/api/searchconsole/pages/${encoded}`),
        ]);
        if (!statsRes.ok || !pagesRes.ok) throw new Error('Erreur données GSC');
        const statsData = await statsRes.json();
        const pagesData = await pagesRes.json();
        if (cancelled) return;
        setGscStats(statsData);
        setGscPages(Array.isArray(pagesData?.pages) ? pagesData.pages : []);
      } catch {
        if (!cancelled) {
          setError('Impossible de charger les statistiques Search Console.');
        }
      } finally {
        if (!cancelled) setGscLoading(false);
      }
    };

    loadGscData();
    return () => {
      cancelled = true;
    };
  }, [activeTab, gscConnected, selectedGscProperty]);

  const publishedCount = useMemo(
    () => serpScores.filter((item) => item?.statut === 'publié').length,
    [serpScores]
  );

  const analyzeCannibalisation = async () => {
    if (!selectedSiteId) return;
    try {
      setLoading(true);
      setError('');
      const response = await apiFetch(`/api/seoanalyse/cannibalisation/${selectedSiteId}`);
      if (!response.ok) throw new Error('Erreur analyse cannibalisation');
      const data = await response.json();
      setCannibalisation({
        groupes: Array.isArray(data?.groupes) ? data.groupes : [],
        recommandationsGlobales: Array.isArray(data?.recommandationsGlobales)
          ? data.recommandationsGlobales
          : [],
      });
    } catch {
      setError('Impossible d analyser la cannibalisation.');
    } finally {
      setLoading(false);
    }
  };

  const analyzeSerp = async () => {
    if (!selectedSiteId) return;
    try {
      setLoading(true);
      setError('');
      const articlesResponse = await apiFetch(`/api/articles/${selectedSiteId}`);
      if (!articlesResponse.ok) throw new Error('Erreur chargement articles');
      const articles = await articlesResponse.json();
      const published = (Array.isArray(articles) ? articles : []).filter(
        (article) => article.statut === 'publié'
      );
      const scores = await Promise.all(
        published.map(async (article) => {
          const response = await apiFetch(`/api/seoanalyse/serp/${article.id}`);
          if (!response.ok) throw new Error('Erreur score SERP');
          return response.json();
        })
      );
      setSerpScores(scores.sort((a, b) => b.score - a.score));
      setOpenedSerpId('');
    } catch {
      setError('Impossible de calculer les scores SERP.');
    } finally {
      setLoading(false);
    }
  };

  const analyzeMaillage = async () => {
    if (!selectedSiteId) return;
    try {
      setLoading(true);
      setError('');
      const response = await apiFetch(`/api/seoanalyse/maillage/${selectedSiteId}`);
      if (!response.ok) throw new Error('Erreur analyse maillage');
      const data = await response.json();
      console.log('Réponse API maillage:', data);
      setMaillage(Array.isArray(data?.suggestions) ? data.suggestions : []);
    } catch {
      setError('Impossible d analyser le maillage interne.');
    } finally {
      setLoading(false);
    }
  };

  const analyzePriorisation = async () => {
    if (!selectedSiteId) return;
    try {
      setLoading(true);
      setError('');
      const response = await apiFetch(`/api/seoanalyse/priorisation/${selectedSiteId}`);
      if (!response.ok) throw new Error('Erreur analyse priorisation');
      const data = await response.json();
      setPriorisation(Array.isArray(data?.sujets) ? data.sujets : []);
      setCreatedTopicIds([]);
    } catch {
      setError('Impossible d analyser les opportunités de priorisation.');
    } finally {
      setLoading(false);
    }
  };

  const createSujetFromOpportunity = async (opportunity, index) => {
    if (!selectedSiteId || creatingTopicId) return;

    const title = String(opportunity?.titreSuggere || '').trim();
    const keyword = String(opportunity?.motCleCible || '').trim();
    if (!title || !keyword) {
      setError('Le titre suggéré et le mot-clé cible sont requis pour créer le sujet.');
      return;
    }

    const topicId = `${title}-${index}`;
    try {
      setError('');
      setCreatingTopicId(topicId);
      const response = await apiFetch('/api/sujets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          site_id: selectedSiteId,
          titre: title,
          mot_cle: keyword,
          description: String(opportunity?.raisonPriorite || '').trim(),
          statut: 'validé',
        }),
      });
      if (!response.ok) throw new Error('Erreur création sujet');
      setCreatedTopicIds((prev) => [...prev, topicId]);
    } catch {
      setError('Impossible de créer ce sujet.');
    } finally {
      setCreatingTopicId('');
    }
  };

  return (
    <section className="seoanalyse-page">
      <header className="seoanalyse-header">
        <h1>SEO Analyse</h1>
        <p>Détectez les conflits SEO, mesurez vos scores SERP et optimisez le maillage interne.</p>
      </header>

      {error && <p className="sites-error">{error}</p>}

      <div className="seoanalyse-toolbar">
        <label htmlFor="seoanalyseSiteSelect">Site sélectionné</label>
        <select
          id="seoanalyseSiteSelect"
          value={selectedSiteId}
          onChange={(event) => setSelectedSiteId(event.target.value)}
          disabled={loading || sites.length === 0}
        >
          {sites.length === 0 ? (
            <option value="">Aucun site disponible</option>
          ) : (
            sites.map((site) => (
              <option key={site.id} value={site.id}>
                {site.nom}
              </option>
            ))
          )}
        </select>
      </div>

      <div className="seoanalyse-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`seoanalyse-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => {
              setActiveTab(tab.id);
              if (tab.id === 'maillage') {
                analyzeMaillage();
              }
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'cannibalisation' && (
        <section className="seoanalyse-section">
          <div className="seoanalyse-actions">
            <button type="button" className="primary-button" onClick={analyzeCannibalisation} disabled={loading}>
              {loading ? 'Analyse...' : 'Analyser'}
            </button>
          </div>
          {cannibalisation.groupes.length === 0 ? (
            <div className="sites-empty-card">
              <p>Aucun conflit détecté pour le moment.</p>
            </div>
          ) : (
            <div className="seoanalyse-cards">
              {cannibalisation.groupes.map((groupe, index) => (
                <article key={`${groupe.motCleConflit || 'groupe'}-${index}`} className={`seo-risk-card ${groupe.niveau || 'ok'}`}>
                  <h3>{groupe.motCleConflit || 'Conflit SEO'}</h3>
                  <p className="seo-risk-reco">
                    {groupe.recommandation || 'modifier'} - {groupe.action || 'Ajuster le ciblage'}
                  </p>
                  <ul className="seo-risk-list">
                    {(groupe.articles || []).map((article) => (
                      <li key={article.id || article.titre}>{article.titre || article.id}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          )}
        </section>
      )}

      {activeTab === 'serp' && (
        <section className="seoanalyse-section">
          <div className="seoanalyse-actions">
            <button type="button" className="primary-button" onClick={analyzeSerp} disabled={loading}>
              {loading ? 'Calcul...' : 'Analyser'}
            </button>
            <span className="seoanalyse-meta">{publishedCount} articles publiés analysés</span>
          </div>
          {serpScores.length === 0 ? (
            <div className="sites-empty-card">
              <p>Aucun score SERP disponible. Lancez une analyse.</p>
            </div>
          ) : (
            <div className="seoanalyse-serp-list">
              {serpScores.map((item) => (
                <article key={item.articleId} className="seo-serp-card">
                  <div className="seo-serp-head">
                    <h3>{item.titre}</h3>
                    <strong className={`seo-serp-score ${scoreClass(item.score)}`}>{item.score}/100</strong>
                  </div>
                  <div className="seo-serp-progress">
                    <div
                      className={`seo-serp-progress-fill ${scoreClass(item.score)}`}
                      style={{ width: `${item.score}%` }}
                    />
                  </div>
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() =>
                      setOpenedSerpId((current) => (current === item.articleId ? '' : item.articleId))
                    }
                  >
                    {openedSerpId === item.articleId ? 'Masquer le détail' : 'Voir le détail'}
                  </button>
                  {openedSerpId === item.articleId && (
                    <div className="seo-serp-details">
                      <p>Longueur: {item.details?.longueurContenu?.valeur || 0} mots</p>
                      <p>Mot-clé dans titre: {item.details?.motCleTitre?.valeur ? 'Oui' : 'Non'}</p>
                      <p>Mot-clé dans H2: {item.details?.motCleH2?.valeur ? 'Oui' : 'Non'}</p>
                      <p>Densité: {item.details?.densiteMotCle?.valeur || 0}%</p>
                      <p>Liens internes: {item.details?.liensInternes?.valeur || 0}</p>
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}
        </section>
      )}

      {activeTab === 'maillage' && (
        <section className="seoanalyse-section">
          <div className="seoanalyse-actions">
            <button type="button" className="primary-button" onClick={analyzeMaillage} disabled={loading}>
              {loading ? 'Analyse...' : 'Analyser le maillage'}
            </button>
          </div>
          {maillage.length === 0 ? (
            <div className="sites-empty-card">
              <p>Aucune suggestion de maillage pour le moment.</p>
            </div>
          ) : (
            <div className="seoanalyse-cards">
              {maillage.map((suggestion, index) => (
                <article key={`${suggestion.sourceId || 's'}-${suggestion.cibleId || 'c'}-${index}`} className="seo-link-card">
                  <h3>{suggestion.ancre || 'Suggestion de lien'}</h3>
                  <p>
                    <strong>Source:</strong> {suggestion.sourceTitre || suggestion.sourceId}
                  </p>
                  <p>
                    <strong>Cible:</strong> {suggestion.cibleTitre || suggestion.cibleId}
                  </p>
                  <p>{suggestion.raison || 'Lien pertinent pour renforcer la thématique.'}</p>
                </article>
              ))}
            </div>
          )}
        </section>
      )}

      {activeTab === 'searchconsole' && (
        <section className="seoanalyse-section">
          <div className="seoanalyse-actions seo-gsc-toolbar">
            <a href="/api/searchconsole/auth" className="primary-button seo-gsc-connect">
              Connecter Google Search Console
            </a>
            {gscConnected && gscSites.length > 0 && (
              <label className="seo-gsc-property-label" htmlFor="gscPropertySelect">
                Propriété
              </label>
            )}
            {gscConnected && gscSites.length > 0 && (
              <select
                id="gscPropertySelect"
                className="seo-gsc-select"
                value={selectedGscProperty}
                onChange={(event) => setSelectedGscProperty(event.target.value)}
                disabled={gscLoading}
              >
                {gscSites.map((site) => (
                  <option key={site.siteUrl} value={site.siteUrl}>
                    {site.siteUrl}
                  </option>
                ))}
              </select>
            )}
          </div>

          {gscConnected === null && (
            <div className="sites-empty-card">
              <p>Chargement de Search Console...</p>
            </div>
          )}

          {gscConnected === false && (
            <div className="sites-empty-card">
              <p>Connectez votre compte Google pour afficher les données Search Console.</p>
            </div>
          )}

          {gscConnected && selectedGscProperty && gscLoading && !gscStats && (
            <div className="sites-empty-card">
              <p>Chargement des statistiques Search Console...</p>
            </div>
          )}

          {gscConnected && selectedGscProperty && gscStats && (
            <>
              <div className="stats-grid seo-gsc-stats-grid">
                <article className="stat-card">
                  <span className="stat-title">Clics (28 j.)</span>
                  <strong className="stat-value">{gscStats.clicks ?? 0}</strong>
                </article>
                <article className="stat-card">
                  <span className="stat-title">Impressions</span>
                  <strong className="stat-value">{gscStats.impressions ?? 0}</strong>
                </article>
                <article className="stat-card">
                  <span className="stat-title">CTR</span>
                  <strong className="stat-value">
                    {gscStats.ctr != null ? `${(Number(gscStats.ctr) * 100).toFixed(2)} %` : '—'}
                  </strong>
                </article>
                <article className="stat-card">
                  <span className="stat-title">Position moy.</span>
                  <strong className="stat-value">
                    {gscStats.position != null ? Number(gscStats.position).toFixed(1) : '—'}
                  </strong>
                </article>
              </div>
              <p className="seoanalyse-meta">
                Période : {gscStats.startDate} → {gscStats.endDate}
              </p>

              <div className="seo-gsc-table-wrap">
                <h3 className="seo-gsc-table-title">Meilleures pages</h3>
                {gscPages.length === 0 ? (
                  <div className="sites-empty-card">
                    <p>Aucune donnée de page pour cette période.</p>
                  </div>
                ) : (
                  <table className="seo-gsc-table">
                    <thead>
                      <tr>
                        <th>Page</th>
                        <th>Clics</th>
                        <th>Impressions</th>
                        <th>CTR</th>
                        <th>Position</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gscPages.map((row) => (
                        <tr key={row.page}>
                          <td className="seo-gsc-page-cell">{row.page}</td>
                          <td>{row.clicks ?? 0}</td>
                          <td>{row.impressions ?? 0}</td>
                          <td>{row.ctr != null ? `${(Number(row.ctr) * 100).toFixed(2)} %` : '—'}</td>
                          <td>{row.position != null ? Number(row.position).toFixed(1) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}

          {gscConnected && gscSites.length === 0 && !gscLoading && (
            <div className="sites-empty-card">
              <p>Aucune propriété trouvée dans Search Console.</p>
            </div>
          )}
        </section>
      )}

      {activeTab === 'priorisation' && (
        <section className="seoanalyse-section">
          <div className="seoanalyse-actions">
            <button type="button" className="primary-button" onClick={analyzePriorisation} disabled={loading}>
              {loading ? 'Analyse...' : 'Analyser les opportunités'}
            </button>
          </div>
          {priorisation.length === 0 ? (
            <div className="sites-empty-card">
              <p>Aucune priorisation disponible. Lancez une analyse des opportunités.</p>
            </div>
          ) : (
            <div className="seoanalyse-cards">
              {priorisation.slice(0, 10).map((opportunity, index) => {
                const score = Math.max(0, Math.min(Number(opportunity?.scoreOpportunite || 0), 10));
                const level = opportunityLevel(score);
                const fillColor = opportunityColor(level);
                const topicId = `${String(opportunity?.titreSuggere || '').trim()}-${index}`;
                const created = createdTopicIds.includes(topicId);
                const creating = creatingTopicId === topicId;

                return (
                  <article key={`${topicId}-${opportunity?.rang || index + 1}`} className="seo-link-card">
                    <h3>
                      #{opportunity?.rang || index + 1} - {opportunity?.titreSuggere || 'Sujet prioritaire'}
                    </h3>
                    <p>
                      <strong>Mot-clé cible:</strong> {opportunity?.motCleCible || '-'}
                    </p>
                    <p>
                      <strong>Score d'opportunité:</strong> {score}/10
                    </p>
                    <div className="seo-serp-progress" aria-hidden="true">
                      <div
                        className="seo-serp-progress-fill"
                        style={{ width: `${score * 10}%`, backgroundColor: fillColor }}
                      />
                    </div>
                    <p>{opportunity?.raisonPriorite || 'Aucune raison fournie.'}</p>
                    <button
                      type="button"
                      className="secondary-button"
                      disabled={creating || created}
                      onClick={() => createSujetFromOpportunity(opportunity, index)}
                    >
                      {created ? 'Sujet créé' : creating ? 'Création...' : 'Créer ce sujet'}
                    </button>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      )}
    </section>
  );
}

export default SeoAnalyse;
